import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Plus, X, Pencil } from '@phosphor-icons/react';
import useLookups from '../hooks/useLookups';
import ListSearch, { filterBySearch } from './ListSearch';
import { formatUtcToIst, formatUtcToIstDate } from '../modules/tickets/utils/datetime';
import { ModuleShell, SkeletonBlock } from './module-ui';

import { BACKEND_URL, apiPath } from '../config/apiBase';
const API = apiPath('/api');

const EMPTY_FORM = {
  name: '',
  trigger: 'ticket_created',
  enabled: true,
  conditions_json: '{\n  "priority": "high"\n}',
  actions_json: '{\n  "notify_manager": true\n}',
};

function AutomationPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin';

  const { lookups: triggerLookups } = useLookups('automation_trigger');

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/automation-rules`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setShowFormModal(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setFormData({
      name: rule.name,
      trigger: rule.trigger,
      enabled: rule.enabled,
      conditions_json: JSON.stringify(rule.conditions || {}, null, 2),
      actions_json: JSON.stringify(rule.actions || {}, null, 2),
    });
    setShowFormModal(true);
    setShowViewModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    let conditions;
    let actions;
    try {
      conditions = JSON.parse(formData.conditions_json || '{}');
      actions = JSON.parse(formData.actions_json || '{}');
    } catch (err) {
      alert('Conditions and Actions must be valid JSON');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const config = { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
      const payload = {
        name: formData.name.trim(),
        trigger: formData.trigger,
        enabled: !!formData.enabled,
        conditions,
        actions,
      };
      if (editing) {
        await axios.patch(`${API}/automation-rules/${editing.rule_id}`, payload, config);
      } else {
        await axios.post(`${API}/automation-rules`, payload, config);
      }
      setShowFormModal(false);
      setFormData(EMPTY_FORM);
      setEditing(null);
      fetchRules();
    } catch (error) {
      alert(error?.response?.data?.detail || (editing ? 'Failed to update rule' : 'Failed to create rule'));
    }
  };

  const filteredRules = filterBySearch(rules, searchQuery, ['rule_id', 'name', 'trigger']);

  return (
    <ModuleShell
      title="Automation"
      subtitle="Manage workflow automation rules"
      headerActions={canEdit ? (
        <button
          onClick={openCreate}
          className="btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          data-testid="create-rule-button"
        >
          <Plus size={18} weight="bold" />
          Create Rule
        </button>
      ) : null}
    >
    <div data-testid="automation-page">

      <ListSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search automation rules…"
        resultCount={filteredRules.length}
        totalCount={rules.length}
      />

      {loading ? (
        <SkeletonBlock />
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Rule ID</th>
                <th>Name</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRules.map((rule) => (
                <tr key={rule.rule_id} data-testid="rule-row">
                  <td style={{ fontFamily: 'IBM Plex Mono, monospace', fontWeight: '600' }}>
                    <button
                      onClick={() => { setSelectedRule(rule); setShowViewModal(true); }}
                      style={{ background: 'none', border: 'none', color: '#002FA7', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 'inherit' }}
                      data-testid="rule-id-link"
                    >
                      {rule.rule_id}
                    </button>
                  </td>
                  <td>{rule.name}</td>
                  <td>{rule.trigger.replace('_', ' ')}</td>
                  <td>
                    <span className={`badge ${rule.enabled ? 'badge-low' : 'badge-closed'}`}>
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>{formatUtcToIstDate(rule.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => { setSelectedRule(rule); setShowViewModal(true); }}
                        className="btn-secondary"
                        style={{ padding: '4px 12px', fontSize: '12px' }}
                        data-testid="view-rule-button"
                      >
                        View
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => openEdit(rule)}
                          className="btn-secondary"
                          style={{ padding: '4px 12px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          data-testid="edit-rule-button"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      {showFormModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          data-testid={editing ? 'edit-rule-modal' : 'create-rule-modal'}
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>
                {editing ? `Edit Rule — ${editing.rule_id}` : 'Create Automation Rule'}
              </h2>
              <button
                onClick={() => setShowFormModal(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                data-testid="rule-modal-close"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Rule Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                  data-testid="rule-name-input"
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Trigger</label>
                <select
                  value={formData.trigger}
                  onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
                  className="input-field"
                  data-testid="rule-trigger-select"
                >
                  {triggerLookups.length === 0 ? (
                    <>
                      <option value="ticket_created">Ticket Created</option>
                      <option value="ticket_updated">Ticket Updated</option>
                      <option value="ticket_assigned">Ticket Assigned</option>
                      <option value="sla_breach">SLA Breach</option>
                    </>
                  ) : (
                    triggerLookups.map((t) => (
                      <option key={t.lookup_id} value={t.key}>{t.label}</option>
                    ))
                  )}
                </select>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Conditions (JSON)</label>
                <textarea
                  value={formData.conditions_json}
                  onChange={(e) => setFormData({ ...formData, conditions_json: e.target.value })}
                  className="input-field"
                  rows="4"
                  style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '12px' }}
                  data-testid="rule-conditions-input"
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500' }}>Actions (JSON)</label>
                <textarea
                  value={formData.actions_json}
                  onChange={(e) => setFormData({ ...formData, actions_json: e.target.value })}
                  className="input-field"
                  rows="4"
                  style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '12px' }}
                  data-testid="rule-actions-input"
                />
              </div>

              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    data-testid="rule-enabled-checkbox"
                  />
                  Enabled
                </label>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} data-testid="submit-rule-button">
                  {editing ? 'Save Changes' : 'Create Rule'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                  data-testid="cancel-rule-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View modal */}
      {showViewModal && selectedRule && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          data-testid="view-rule-modal"
        >
          <div style={{ background: 'white', padding: '32px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ fontSize: '15px', fontWeight: '600' }}>{selectedRule.rule_id}</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {canEdit && (
                  <button onClick={() => openEdit(selectedRule)} className="btn-primary" style={{ padding: '6px 16px' }} data-testid="view-modal-edit-button">
                    Edit
                  </button>
                )}
                <button onClick={() => setShowViewModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                  <X size={24} />
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Rule Name</p>
              <p style={{ fontSize: '16px', fontWeight: '600' }}>{selectedRule.name}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '8px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Trigger</p>
                <p style={{ fontSize: '12px', fontWeight: '500', textTransform: 'capitalize' }}>{selectedRule.trigger.replace('_', ' ')}</p>
              </div>
              <div>
                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Status</p>
                <span className={`badge ${selectedRule.enabled ? 'badge-low' : 'badge-closed'}`}>
                  {selectedRule.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Conditions</p>
              <pre style={{ background: '#F9FAFB', padding: '12px', fontSize: '12px', fontFamily: 'IBM Plex Mono, monospace', border: '1px solid #E5E7EB', overflow: 'auto' }}>
                {JSON.stringify(selectedRule.conditions, null, 2)}
              </pre>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Actions</p>
              <pre style={{ background: '#F9FAFB', padding: '12px', fontSize: '12px', fontFamily: 'IBM Plex Mono, monospace', border: '1px solid #E5E7EB', overflow: 'auto' }}>
                {JSON.stringify(selectedRule.actions, null, 2)}
              </pre>
            </div>

            <div>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Created At</p>
              <p style={{ fontSize: '12px' }}>{formatUtcToIst(selectedRule.created_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

export default AutomationPage;
